import { TRACKING_BUNDLE_URL } from '../config'
import { EntryMode, FlowMetaData, isEditEntryMode, RealEstateCategory } from '../@types/types'
import { anyObject, TrackingFunction } from '../@types/tracking-types'
import { MultimediaUploaderType } from '../@types/multimedia-types'
import { FlowInformation } from '../@types/flow-types'

const TEALIUM_TIMEOUT = 1000

const PAGE_TITLE_PREFIX = 'is24.de.offer.central_listings'

type CommonTrackingParameters = {
  ga_cd_test_ppa?: string
}

let commonTrackingParameters: CommonTrackingParameters = {}

declare global {
  interface Window {
    IS24?: {
      TEALIUM: {
        tracking: {
          report: TrackingFunction
        }
      }
    }
  }
}
export const initTracking = () => {
  const script = document.createElement('script')
  script.src = TRACKING_BUNDLE_URL
  script.id = 'tealium-script'
  script.async = true
  document.body.appendChild(script)
}

export const setCommonTrackingParameters = (parameters?: CommonTrackingParameters) => {
  commonTrackingParameters = parameters ?? {}
}

const isTrackingLoaded = () =>
  window.IS24 !== undefined && window.IS24.TEALIUM !== undefined && window.IS24.TEALIUM.tracking !== undefined

const trackEvent = (event: anyObject) => isTrackingLoaded() && window.IS24?.TEALIUM.tracking.report(event)

type TrackingParams = {
  eventName?: string
  flowInformation?: FlowInformation
  entryMode?: string
  pageTitle?: string
  experiment?: string
  eventParameter?: string
  moreEventParameters?: {
    event_parameter_2?: string | number
    event_parameter_3?: string | number
    event_parameter_4?: string | number
    ga_cd_realestate_object_id?: string
    ga_cd_test_ppa?: string
  }
  eventParameter1?: string | null
}

type PageViewMoreEventParameters = {
  ga_cd_test_ppa?: string
}

const mergeCommonTrackingParameters = <T extends object>(parameters: T): T & CommonTrackingParameters => ({
  ...commonTrackingParameters,
  ...parameters,
})

const reportPageView = (event: anyObject) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`page_view: ${event.pag_pagetitle}`)
    console.log('event', event)
  }
  if (!isTrackingLoaded()) return

  window.IS24!!.TEALIUM.tracking.report(event, { reportType: 'UTAG_VIEW' })
}

export const tracking = (
  {
    eventName,
    flowInformation = undefined,
    entryMode = undefined,
    pageTitle = undefined,
    experiment = undefined,
    eventParameter = undefined,
    moreEventParameters = {},
    eventParameter1 = 'central_listings_flow',
  }: TrackingParams,
  awaitTracking: boolean = false
) => {
  const mergedMoreEventParameters = mergeCommonTrackingParameters(moreEventParameters)

  const trackingCallback = () => {
    const pageTitleValue = pageTitle ?? getPageTitle(flowInformation?.metaData, entryMode, experiment)
    const data = {
      pag_pagetitle: pageTitleValue,
      // GA4
      event_name: eventName,
      event_product: 'ppa',
      ...(eventParameter1 !== null ? { event_parameter_1: eventParameter1 } : {}),
      obj_scoutId: mergedMoreEventParameters.ga_cd_realestate_object_id,
      ...(eventParameter ? { event_parameter_2: eventParameter } : {}),
      ...mergedMoreEventParameters,
    }
    if (process.env.NODE_ENV === 'development') {
      console.log(`event: ${JSON.stringify(data)}`)
    }
    trackEvent(data)
  }
  if (awaitTracking) {
    trackingCallback()
  } else {
    setTimeout(trackingCallback, isTrackingLoaded() ? 0 : TEALIUM_TIMEOUT)
  }
}

export const getPageTitle = (flow?: FlowMetaData, entryMode?: string, page?: string) => {
  const segmentation = flow?.segmentation
  const category = flow?.category
  const transaction = flow?.transaction

  return [
    PAGE_TITLE_PREFIX,
    segmentation && segmentation.toLowerCase(),
    category && (category === RealEstateCategory.PRODUCTION_HALL ? 'industry' : category.toLowerCase()),
    transaction && transaction.toLowerCase(),
    entryMode && (isEditEntryMode(entryMode) ? 'edit' : 'create'),
    page,
  ]
    .filter((it) => it)
    .join('.')
}

export const trackPageView = (
  flowInformation?: FlowInformation,
  entryMode?: EntryMode | 'create',
  page?: string,
  obj_scoutId?: number,
  pageTitle?: string,
  moreEventParameters?: PageViewMoreEventParameters
) => {
  const mergedMoreEventParameters = mergeCommonTrackingParameters(moreEventParameters ?? {})
  const pageTitleValue = pageTitle ?? getPageTitle(flowInformation?.metaData, entryMode, page)

  const event = {
    pag_pagetitle: pageTitleValue,
    obj_scoutId: obj_scoutId,
    ...mergedMoreEventParameters,
  }

  reportPageView(event)
}

export const trackingOnPageLoad = (
  flowInformation?: FlowInformation,
  entryMode?: string,
  page?: string,
  obj_scoutId?: number,
  pageTitle?: string,
  moreEventParameters?: PageViewMoreEventParameters
) => {
  const mergedMoreEventParameters = mergeCommonTrackingParameters(moreEventParameters ?? {})

  setTimeout(
    () => {
      const pageTitleValue = pageTitle ?? getPageTitle(flowInformation?.metaData, entryMode, page)
      const event = {
        pag_pagetitle: pageTitleValue,
        obj_scoutId: obj_scoutId,
        ...mergedMoreEventParameters,
      }
      reportPageView(event)
    },
    isTrackingLoaded() ? 0 : TEALIUM_TIMEOUT
  )
}

export const trackingOnAttachmentUpload = (
  flowInformation: FlowInformation,
  attachmentType: MultimediaUploaderType,
  entryMode?: string
) => {
  switch (attachmentType) {
    case MultimediaUploaderType.FLOOR_PLAN: {
      tracking({ eventName: 'attachment_floorplan_upload', flowInformation, entryMode })
      break
    }
    case MultimediaUploaderType.DOCUMENT: {
      tracking({ eventName: 'attachment_document_upload', flowInformation, entryMode })
      break
    }
    case MultimediaUploaderType.PICTURE: {
      tracking({ eventName: 'attachment_image_upload', flowInformation, entryMode })
      break
    }
  }
}

export const trackingPropertyMapping = {
  hideStreetAndStreetNumber: 'visibility',
  buildingType: 'realestate_subtype',
  totalFloorsNumber: 'number_of_floors',
  mainFloorSpace: 'floor_space',
  separableSpace: 'min_divisible',
  price: 'baserent',
  handicappedAccessible: 'barrier_free',
  basement: 'cellar',
  elevator: 'lift',
  monthly: 'office_rent_durations_monthly',
  longTerm: 'office_rent_durations_longTerm',
  yearly: 'office_rent_durations_yearly',
  weekly: 'office_rent_durations_weekly',
  constructionCondition: 'condition',
  constructionYear: 'year',
  constructionLastModernization: 'last_modernization',
  constructionEnergySource: 'energy_source',
  constructionHeatingType: 'heating_type',
}

export type TrackingPropertyMappingType = keyof typeof trackingPropertyMapping
